import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { fetchGiftMessage } from 'lib/core/data/quote/giftMessage'
import { useCart } from 'lib/core/hooks/useCart'
import { useGiftMessage } from 'lib/core/hooks/useGiftMessage'

export interface GiftMessageFormValues {
  sender: string
  recipient: string
  message: string
}

interface GiftMessageState {
  order?: GiftMessageFormValues
  items?: Record<string, GiftMessageFormValues>
}

export const useGiftMessageForm = () => {
  const dispatch = useDispatch()
  const { cart, cartId } = useCart()
  const { saveGiftMessage, giftMessage } = useGiftMessage()

  const [addGiftMessage, setAddGiftMessage] = useState(false)
  const [addEntireOrder, setAddEntireOrder] = useState(false)
  const [addIndividually, setAddIndividually] = useState(false)
  const [data, setData] = useState<GiftMessageState>({})
  const cartItems = cart?.data?.items ?? []
  const isLoading = Boolean(!giftMessage || giftMessage?.isFirstLoad || cart?.isFetching)

  useEffect(() => {
    if (!cartId) {
      return
    }

    dispatch(fetchGiftMessage(cartId))
  }, [cartId, dispatch])

  useEffect(() => {
    if (!addGiftMessage) {
      setData({})
    }
  }, [addGiftMessage, setData])

  useEffect(() => {
    if (!addEntireOrder) {
      setData(state => ({
        ...state,
        order: undefined
      }))
    }
  }, [addEntireOrder, setData])

  useEffect(() => {
    if (!addIndividually) {
      setData(state => ({
        ...state,
        items: undefined
      }))
    }
  }, [addIndividually, setData])

  useEffect(() => {
    if (isLoading) {
      return
    }

    const sender = giftMessage?.data.sender ?? ''
    const recipient = giftMessage?.data.recipient ?? ''
    const message = giftMessage?.data.message ?? ''
    const hasItems = Boolean(giftMessage?.data.items.length)

    setData({
      order: {
        sender,
        recipient,
        message
      },
      items: giftMessage?.data.items.reduce((obj: Record<string, GiftMessageFormValues>, item) => {
        obj[item.cartItemId] = {
          sender: item.sender,
          recipient: item.recipient,
          message: item.message
        }
        return obj
      }, {})
    })

    setAddGiftMessage(Boolean(sender || recipient || message || hasItems))
    setAddEntireOrder(Boolean(sender || recipient || message))
    setAddIndividually(Boolean(hasItems))
  }, [
    giftMessage,
    isLoading,
    setData,
    setAddGiftMessage,
    setAddEntireOrder,
    setAddIndividually
  ])

  useEffect(() => {
    if (!data) {
      return
    }

    const giftMessageInput = {
      sender: data.order?.sender ?? '',
      recipient: data.order?.recipient ?? '',
      message: data.order?.message ?? '',
      items: Object.keys(data?.items ?? []).map((cartItemId) => {
        return {
          cartItemId: parseInt(cartItemId),
          sender: data.items?.[cartItemId]?.sender ?? '',
          recipient: data.items?.[cartItemId]?.recipient ?? '',
          message: data.items?.[cartItemId]?.message ?? ''
        }
      })
    }

    saveGiftMessage(giftMessageInput, {
      debounce: true
    })
  }, [
    data,
    saveGiftMessage
  ])

  return {
    cartItems,
    hasItems: Boolean(cartItems.length),
    addGiftMessage,
    setAddGiftMessage,
    addEntireOrder,
    setAddEntireOrder,
    addIndividually,
    setAddIndividually,
    data,
    setData,
    isLoading
  }
}
